import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { BookOpen, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bible Coverage Dashboard (super_admin + moderator).
 * Renders per-translation totals and a per-book coverage matrix pulled from
 * GET /api/admin/bible-coverage.
 */
const BibleCoverageDashboard = ({ language = 'en' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/bible-coverage`, {
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      console.error('bible-coverage load', err);
      toast.error(
        language === 'es'
          ? 'Error al cargar cobertura bíblica'
          : 'Failed to load Bible coverage'
      );
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data?.matrix) return [];
    if (!filter.trim()) return data.matrix;
    const q = filter.toLowerCase();
    return data.matrix.filter((r) => r.book.toLowerCase().includes(q));
  }, [data, filter]);

  const heatColor = (pct) => {
    if (pct >= 99) return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
    if (pct >= 80) return 'bg-lime-500/15 text-lime-400 border-lime-500/30';
    if (pct >= 30) return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
    if (pct > 0)  return 'bg-rose-500/20 text-rose-300 border-rose-500/30';
    return 'bg-muted text-muted-foreground border-muted';
  };

  if (!data && !loading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        {language === 'es' ? 'Sin datos' : 'No data'}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bible-coverage-dashboard">
      {/* Translation totals */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <CardTitle className="text-base sm:text-lg">
              {language === 'es' ? 'Traducciones' : 'Translations'}
            </CardTitle>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="bible-coverage-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            {language === 'es' ? 'Actualizar' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{language === 'es' ? 'Traducción' : 'Translation'}</TableHead>
                <TableHead className="text-right">
                  {language === 'es' ? 'Versículos' : 'Verses'}
                </TableHead>
                <TableHead className="text-right">
                  {language === 'es' ? 'Cobertura' : 'Coverage'}
                </TableHead>
                <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.translations || []).map((t) => (
                <TableRow key={t.translation} data-testid={`bible-tr-row-${t.translation}`}>
                  <TableCell className="font-medium">{t.translation}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {t.verses.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {t.percent}%
                  </TableCell>
                  <TableCell>
                    {t.complete ? (
                      <Badge
                        variant="outline"
                        className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {language === 'es' ? 'Completa' : 'Complete'}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="bg-amber-500/15 text-amber-400 border-amber-500/30"
                      >
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {language === 'es' ? 'Parcial' : 'Sparse'}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Per-book heat matrix for fully-seeded translations */}
      <Card>
        <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="text-base sm:text-lg">
            {language === 'es' ? 'Cobertura por libro' : 'Per-book coverage'}
          </CardTitle>
          <Input
            placeholder={language === 'es' ? 'Filtrar libro…' : 'Filter book…'}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs text-sm"
            data-testid="bible-coverage-filter"
          />
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{language === 'es' ? 'Libro' : 'Book'}</TableHead>
                <TableHead className="text-right text-xs">Ch</TableHead>
                <TableHead className="text-right text-xs">
                  {language === 'es' ? 'Esperado' : 'Expected'}
                </TableHead>
                {(data?.seeded_translations || []).map((tr) => (
                  <TableHead key={tr} className="text-center text-xs">
                    {tr}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.book}>
                  <TableCell className="font-medium whitespace-nowrap">
                    {r.book}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.chapters}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.expected}
                  </TableCell>
                  {(data?.seeded_translations || []).map((tr) => {
                    const cell = r.coverage?.[tr] || { verses: 0, percent: 0 };
                    return (
                      <TableCell key={tr} className="text-center">
                        <Badge
                          variant="outline"
                          className={`${heatColor(cell.percent)} text-xs tabular-nums`}
                          title={`${cell.verses} / ${r.expected} verses`}
                          data-testid={`bible-cov-${tr}-${r.book.replace(/\s+/g,'_')}`}
                        >
                          {cell.percent}%
                        </Badge>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        {language === 'es'
          ? 'Los porcentajes comparan contra los conteos canónicos protestantes (66 libros, 31 102 versículos).'
          : 'Percentages are measured against the canonical Protestant counts (66 books, 31,102 verses).'}
      </p>
    </div>
  );
};

export default BibleCoverageDashboard;
